import { resolveSettingsLocation } from '@/lib/settings-navigation';
import { ACCESS_REQUIREMENTS, type AccessRequirement } from '@/lib/access-requirements';
import { companySupportsWaitingList } from '@/lib/company';
import type { CompanyAttendanceMode, CompanyOperationMode } from '@/types';
import type { LucideIcon } from 'lucide-react';
import {
  Building2,
  Calendar,
  LayoutDashboard,
  ListOrdered,
  Package,
  Scissors,
  Settings,
  SlidersHorizontal,
  Users,
} from 'lucide-react';

export interface NavigationSubItem {
  id: string;
  icon: LucideIcon;
  label: string;
  description: string;
  path: string;
  access?: AccessRequirement;
  requiresWaitingList?: boolean;
}

export interface NavigationSection {
  id: string;
  icon: LucideIcon;
  label: string;
  description: string;
  path?: string;
  access?: AccessRequirement;
  items: NavigationSubItem[];
}

const NAV_SECTIONS: NavigationSection[] = [
  {
    id: 'dashboard',
    icon: LayoutDashboard,
    label: 'Dashboard',
    description: 'Acompanhe os números e a direção do negócio.',
    path: '/dashboard',
    access: ACCESS_REQUIREMENTS.DASHBOARD,
    items: [],
  },
  {
    id: 'attendance',
    icon: Calendar,
    label: 'Atendimento',
    description: 'Tudo que faz a rotina diária acontecer.',
    items: [
      {
        id: 'agenda',
        icon: Calendar,
        label: 'Agenda',
        description: 'Grade de agendamentos por dia e semana, separada da operação em tempo real.',
        path: '/agenda',
        access: ACCESS_REQUIREMENTS.AGENDA_READ,
      },
      {
        id: 'walk-in-queue',
        icon: ListOrdered,
        label: 'Filas',
        description: 'Atendimento por ordem de chegada, separado por profissional.',
        path: '/walk-in-queue',
        access: ACCESS_REQUIREMENTS.WALK_IN_QUEUE_READ,
        requiresWaitingList: true,
      },
    ],
  },
  {
    id: 'catalog',
    icon: Scissors,
    label: 'Catálogo',
    description: 'O que sua empresa oferece aos clientes.',
    items: [
      {
        id: 'services',
        icon: Scissors,
        label: 'Serviços',
        description: 'Cortes, procedimentos e durações.',
        path: '/services',
        access: ACCESS_REQUIREMENTS.SERVICES_READ,
      },
      {
        id: 'products',
        icon: Package,
        label: 'Produtos',
        description: 'Produtos disponíveis para venda durante o atendimento.',
        path: '/products',
        access: ACCESS_REQUIREMENTS.PRODUCTS_READ,
      },
    ],
  },
  {
    id: 'customers',
    icon: Users,
    label: 'Clientes',
    description: 'Relacionamento, histórico e contexto dos clientes.',
    path: '/customers',
    access: ACCESS_REQUIREMENTS.CUSTOMERS_READ,
    items: [],
  },
  {
    id: 'team',
    icon: Users,
    label: 'Equipe',
    description: 'Profissionais que aparecem na agenda do MVP.',
    items: [
      {
        id: 'professionals',
        icon: Users,
        label: 'Profissionais',
        description: 'Quem atende e opera na empresa.',
        path: '/professionals',
        access: ACCESS_REQUIREMENTS.PROFESSIONALS_READ,
      },
    ],
  },
  {
    id: 'settings',
    icon: Settings,
    label: 'Configurações',
    description: 'Regras internas, horários e acessos.',
    items: [
      {
        id: 'company-settings',
        icon: Building2,
        label: 'Empresa',
        description: 'Dados gerais e endereço da empresa.',
        path: '/settings?section=company&tab=company',
        access: ACCESS_REQUIREMENTS.COMPANY_READ,
      },
      {
        id: 'attendance-settings',
        icon: SlidersHorizontal,
        label: 'Atendimento',
        description: 'Horários, agendamentos e filas.',
        path: '/settings?section=attendance&tab=operation',
        access: ACCESS_REQUIREMENTS.COMPANY_READ,
      },
      {
        id: 'public-page-settings',
        icon: LayoutDashboard,
        label: 'Página pública',
        description: 'Apresentação, contatos e visibilidade.',
        path: '/settings?section=public-page&tab=presentation',
        access: ACCESS_REQUIREMENTS.COMPANY_READ,
      },
      {
        id: 'access-settings',
        icon: Users,
        label: 'Acessos',
        description: 'Usuários e permissões da equipe.',
        path: '/rbac',
        access: ACCESS_REQUIREMENTS.RBAC_MANAGE,
      },
    ],
  },
];

export function isNavigationItemActive(pathname: string, item: NavigationSubItem) {
  const [currentPathname, currentSearch = ''] = pathname.split('?');
  const [itemPathname, itemSearch = ''] = item.path.split('?');
  const pathMatches = currentPathname === itemPathname || currentPathname.startsWith(`${itemPathname}/`);

  if (!pathMatches || !itemSearch) return pathMatches;

  const currentSection =
    currentPathname === '/settings'
      ? resolveSettingsLocation(new URLSearchParams(currentSearch)).section
      : new URLSearchParams(currentSearch).get('section');
  const itemSection = new URLSearchParams(itemSearch).get('section');
  return !itemSection || currentSection === itemSection;
}

export function isNavigationSectionActive(pathname: string, section: NavigationSection) {
  if (section.path) {
    return pathname === section.path || pathname.startsWith(`${section.path}/`);
  }

  return section.items.some((item) => isNavigationItemActive(pathname, item));
}

export function getNavigationSections({
  activeCompany,
  canAccess,
}: {
  activeCompany?: { operationMode?: CompanyOperationMode | null; attendanceMode?: CompanyAttendanceMode | null } | null;
  canAccess: (requirement: AccessRequirement) => boolean;
}) {
  return NAV_SECTIONS.reduce<typeof NAV_SECTIONS>((sections, section) => {
    if (section.path) {
      if (!section.access || canAccess(section.access)) {
        sections.push(section);
      }
      return sections;
    }

    const items = section.items.filter((item) => {
      if (item.requiresWaitingList && !companySupportsWaitingList(activeCompany)) {
        return false;
      }

      return item.access ? canAccess(item.access) : true;
    });

    if (items.length > 0) {
      sections.push({ ...section, items });
    }

    return sections;
  }, []);
}
